import React from "react";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { rest, t, getLocale } from "@auto/services";
import { displayList } from "@auto/widgets/ListComponent";
import { createAdjudicationListFacets, normalizeAdjudicationListFilters } from "./AdjudicationListFacets";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";
import styles from "./AdjudicationPage.module.styl";

const NINU = "NINU_CERTIFICATE";
const VOTER = "VOTER_REGISTRATION";
const API = (kind, path = "") => `${getServiceUri()}adjudication/${kind}/${path}`;
const kinds = [NINU, VOTER];
const APPROVABLE_VOTER_REASONS = new Set(["BIOGRAPHICAL_MISMATCH", "BIOMETRIC_MISMATCH"]);
const checkKind = (kind) => {
  if (!kinds.includes(kind)) throw new Error(`Unsupported adjudication case kind: ${kind}`);
};
const fmtDate = (value) => value ? new Intl.DateTimeFormat(getLocale(), {
  dateStyle: "medium", timeStyle: "short",
}).format(new Date(value)) : "—";
const text = (value) => (value === null || value === undefined || String(value).trim() === "")
  ? "—" : String(value);
const label = (key, value) => t(`adjudication.${key}.${value}`);

const Status = ({ status }) => <span className={`${styles.status} ${styles[status] || ""}`}>
  {label("status", status)}
</span>;

const Candidate = ({ data, image, title }) => (
  <section className={styles.candidate}>
    <div className={styles.candidateTitle}>{title}</div>
    <div className={styles.identityHeader}>
      {image ? <img className={styles.portrait} src={image} alt={title} />
        : <div className={styles.noPortrait}>{t("adjudication.noPhoto")}</div>}
      <div className={styles.identitySummary}>
        <strong>{[data?.firstName, data?.lastName].filter(Boolean).join(" ") || text(data?.lastName)}</strong>
        <span>NINU: {text(data?.nationalIdentifier)}</span>
        <span>{t("adjudication.dateOfBirth")}: {text(data?.dateOfBirth)}</span>
      </div>
    </div>
  </section>
);

const Comparison = ({ submitted, oni }) => {
  const keys = [
    ["nationalIdentifier", "ninu"],
    ["firstName", "firstName"],
    ["lastName", "lastName"],
    ["dateOfBirth", "dateOfBirth"],
  ];
  return <div className={styles.comparison}>
    <div className={styles.comparisonTitle}>{t("adjudication.comparison")}</div>
    <table>
      <thead><tr><th>{t("adjudication.field")}</th><th>{t("adjudication.request")}</th><th>{t("adjudication.oni")}</th></tr></thead>
      <tbody>{keys.map(([key, name]) => {
        const different = submitted?.[key] && oni?.[key]
          && String(submitted[key]).trim().toLocaleUpperCase() !== String(oni[key]).trim().toLocaleUpperCase();
        return <tr key={key} className={different ? styles.difference : ""}>
          <td>{name === "ninu" ? "NINU" : label("field", name)}</td>
          <td>{text(submitted?.[key])}</td>
          <td>{text(oni?.[key])}</td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
};

// The backoffice's standard ListComponent owns search, filters, sorting, paging,
// toolbar, empty/error state and row action layout. Only the data projection is specific.
const LIST_PAGE_SIZE = 50;
const listProjections = new Map();

const adjudicationListProjection = (kind) => {
  if (!listProjections.has(kind)) {
    listProjections.set(kind, {
      storageKey: `adjudication.${kind}`,
      facets: createAdjudicationListFacets(kind),
      columns: [
        {
          title: "adjudication.submittedAt", field: "createdAt", sortSelector: "createdAt",
          render: (row) => fmtDate(row.createdAt),
        },
        { title: "person.nationalId", field: "nationalIdentifier", sortSelector: "nationalIdentifier" },
        {
          title: "adjudication.applicant", field: "applicantName",
          sorting: false, filtering: false,
          render: (row) => row.applicantName,
        },
        {
          title: "adjudication.reasonLabel", field: "reason", sortSelector: "reason",
          render: (row) => label("reason", row.reason),
        },
        {
          title: "adjudication.statusLabel", field: "status", sortSelector: "status",
          render: (row) => <Status status={row.status} />,
        },
        { title: "common.actions", field: "__actions", sorting: false, filtering: false },
      ],
      actions: (row, gui) => (
        <Tooltip title={t("adjudication.examine")}>
          <IconButton size="small" aria-label={t("adjudication.examine")}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              gui.goTo(`${kind === NINU ? "/person" : "/voter-application"}/adjudications/${row.id}`);
            }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      fetch: async (query) => {
        const page = Number.isInteger(query?.page) && query.page >= 0 ? query.page : 0;
        const pageSize = Number.isInteger(query?.pageSize) && query.pageSize > 0
          ? query.pageSize : LIST_PAGE_SIZE;
        const sort = (Array.isArray(query?.sort) ? query.sort : [])
          .map((entry) => ({
            selector: entry.column?.sortSelector || entry.field,
            direction: entry.direction,
          }))
          .filter((entry) => entry.selector && ["asc", "desc"].includes(entry.direction));
        const response = await rest.request(API(kind, "search"), "POST", {
          status: normalizeAdjudicationListFilters(query?.filters).status,
          search: String(query?.search || "").trim(),
          limit: pageSize,
          offset: page * pageSize,
          sort,
        });
        return {
          data: (response.rows || []).map((row) => ({
            ...row,
            applicantName: [row.request?.firstName, row.request?.lastName]
              .filter(Boolean).join(" ") || "—",
          })),
          totalCount: Number(response.total || 0),
          page,
        };
      },
    });
  }
  return listProjections.get(kind);
};

const CaseDetails = ({ gui, kind, id }) => {
  const [item, setItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [dialog, setDialog] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true); setError(null);
    try { setItem(await rest.request(API(kind, id), "GET")); }
    catch (exception) { setError(exception.message || String(exception)); }
    finally { setLoading(false); }
  }, [kind, id]);
  React.useEffect(() => { void load(); }, [load]);
  const mayDecide = can(gui, kind === NINU ? "ADJUDICATE_NINU" : "ADJUDICATE_VOTER_APPLICATION");
  const canApprove = kind === NINU
    ? Boolean(item?.personGid && item?.oniPhotoId)
    : APPROVABLE_VOTER_REASONS.has(item?.reason)
        && (item?.reason !== "BIOMETRIC_MISMATCH" || Boolean(item?.oniPhotoId));
  const confirm = async () => {
    if (reason.trim().length < 5) return;
    setSaving(true); setError(null);
    try {
      const updated = await rest.request(API(kind, `${id}/decision`), "POST", {
        decision: dialog, reason: reason.trim(), revision: item.revision,
      });
      setItem(updated); setDialog(null); setReason("");
    } catch (exception) { setError(exception.message || String(exception)); }
    finally { setSaving(false); }
  };
  const back = () => gui.goTo(kind === NINU ? "/person/adjudications" : "/voter-application/adjudications");
  return <main className={styles.page}>
    <Button onClick={back}>{t("adjudication.back")}</Button>
    {error && <Alert severity="error">{error}</Alert>}
    {loading && <CircularProgress />}
    {item && <>
      <header className={styles.header}>
        <div><h1>{t("adjudication.caseTitle")}</h1>
          <p>{t("adjudication.submittedAt")}: {fmtDate(item.createdAt)} · {item.id}</p></div>
        <Status status={item.status} />
      </header>
      <section className={styles.evidence}>
        <Candidate title={t("adjudication.request")}
          data={item.request}
          image={item.hasRequestPortrait ? API(kind, `${id}/portrait`) : null} />
        <Candidate title={t("adjudication.oni")}
          data={item.oni}
          image={item.oniPhotoId ? `${getServiceUri()}person-photo/data/${item.oniPhotoId}` : null} />
      </section>
      <Comparison submitted={item.request} oni={item.oni} />
      <div className={styles.reason}>
        <strong>{t("adjudication.reasonLabel")}:</strong> {label("reason", item.reason)}
        {kind === NINU && <p>{t("adjudication.biographyCheck")}: {item.request?.biographicalMatched
          ? t("adjudication.match") : t("adjudication.noMatch")}
          {" · "}{t("adjudication.faceCheck")}: {item.request?.faceMatched == null
            ? t("adjudication.unavailable") : item.request.faceMatched
              ? t("adjudication.match") : t("adjudication.noMatch")}
        </p>}
      </div>
      {item.voterApplicationGid && <Button onClick={() => gui.goTo(`/voter-application/${item.voterApplicationGid}`)}>
        {t("adjudication.openVoterApplication")}</Button>}
      {item.status === "PENDING_REVIEW" && mayDecide && <div className={styles.actions}>
        {canApprove && <Button variant="contained" onClick={() => setDialog("APPROVE")}>{t("adjudication.approve")}</Button>}
        {!canApprove && <span className={styles.correctionRequired}>{t("adjudication.correctionRequired")}</span>}
        <Button color="error" variant="outlined" onClick={() => setDialog("REJECT")}>{t("adjudication.reject")}</Button>
      </div>}
      {item.status !== "PENDING_REVIEW" && <div className={styles.decision}>
        <strong>{t("adjudication.decision")}: </strong><Status status={item.status} />
        <p>{t("adjudication.reviewer")}: {text(item.reviewerBackofficeUserId)} · {fmtDate(item.reviewedAt)}</p>
        {item.decisionReason && <p>{item.decisionReason}</p>}
      </div>}
    </>}
    <Dialog open={Boolean(dialog)} onClose={saving ? undefined : () => setDialog(null)} maxWidth="sm" fullWidth>
      <DialogTitle>{dialog === "APPROVE" ? t("adjudication.approve") : t("adjudication.reject")}</DialogTitle>
      <DialogContent>
        <p>{t("adjudication.decisionExplanation")}</p>
        <TextField autoFocus multiline rows={4} fullWidth required
          label={t("adjudication.decisionReason")} value={reason}
          onChange={(event) => setReason(event.target.value)} inputProps={{ maxLength: 4000 }} />
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={() => setDialog(null)}>{t("common.cancel")}</Button>
        <Button variant="contained" color={dialog === "REJECT" ? "error" : "primary"}
          disabled={saving || reason.trim().length < 5} onClick={confirm}>
          {saving ? <CircularProgress size={18} /> : t("adjudication.confirm")}</Button>
      </DialogActions>
    </Dialog>
  </main>;
};

export const displayAdjudicationList = (gui, kind) => {
  checkKind(kind);
  return displayList(gui, adjudicationListProjection(kind));
};
export const displayAdjudicationDetails = (gui, kind, id) => {
  checkKind(kind);
  return { uuid: `adjudication-${kind}-${id}`, view: () => <CaseDetails gui={gui} kind={kind} id={id} /> };
};
